import { Link } from "@tanstack/react-router";
import { Card, CardHeader, CardBody } from "@flint/ui";
import { StoreAdminLayout } from "./store-admin-layout.tsx";

/** KPI tiles the dashboard will show once M8 wires the read-models. */
const KPIS = [
  { label: "Sales this month", hint: "vs last month" },
  { label: "Open orders", hint: "value in flight" },
  { label: "Pending quotes", hint: "expiring this week" },
  { label: "Returns (30d)", hint: "damaged · restockable" },
] as const;

/** Modules a rep reaches most often from the landing screen. */
const QUICK_LINKS = [
  { to: "/store/admin/products", label: "Products", note: "Add and publish a SKU" },
  { to: "/store/admin/orders", label: "Orders", note: "The core loop" },
  { to: "/store/admin/buyers", label: "Buyers", note: "Who has ordered" },
  { to: "/store/admin/quotes", label: "Quotes", note: "Priced line sheets" },
] as const;

/**
 * Store admin dashboard (Commerce, V1.0.1) — the landing screen for the module,
 * reached from the launcher "Store" tile and the first sidebar link.
 *
 * KPI tiles and the attention list are placeholders until M8 builds the
 * read-models; the quick links below them are live navigation today.
 *
 * @see prototype/store/admin/all.html#dashboard — the wireframe
 * @see docs/ecom/v1.0.1-tasks.md — M8 Reports + Dashboard
 */
export function StoreAdminHome() {
  return (
    <StoreAdminLayout breadcrumbs={[{ label: "Store" }]}>
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">Store admin</h1>
        <p className="mt-1.5 text-sm text-muted">Wholesale operations</p>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {KPIS.map((k) => (
          <Card key={k.label}>
            <CardBody>
              <div className="text-xs font-medium text-muted">{k.label}</div>
              {/* Placeholder stays muted; when M8 wires real values this becomes
                  `text-ink` with the digits in a `.num` span (the ₹ stays sans). */}
              <div className="mt-1 truncate text-xl font-bold leading-tight text-muted">—</div>
              <div className="mt-1 text-xs text-muted">{k.hint}</div>
            </CardBody>
          </Card>
        ))}
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <h2 className="text-sm font-semibold text-ink">Sales — last 6 months</h2>
          </CardHeader>
          <CardBody>
            <div className="grid h-40 place-items-center rounded-md border border-dashed border-line text-xs text-muted">
              Chart arrives with the reports read-models
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold text-ink">Attention</h2>
          </CardHeader>
          <CardBody>
            <p className="text-sm text-muted">
              Quotes expiring, orders past dispatch, low stock and unresolved returns will surface
              here once those modules land.
            </p>
          </CardBody>
        </Card>
      </div>

      <Card className="mt-4">
        <CardHeader>
          <h2 className="text-sm font-semibold text-ink">Jump to</h2>
        </CardHeader>
        <CardBody>
          <ul className="divide-y divide-line rounded-md border border-line">
            {QUICK_LINKS.map((l) => (
              <li key={l.to} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                <Link to={l.to} className="text-sm font-medium text-sky-700 hover:underline">
                  {l.label}
                </Link>
                <span className="text-xs text-muted">{l.note}</span>
              </li>
            ))}
          </ul>
        </CardBody>
      </Card>
    </StoreAdminLayout>
  );
}
